/**
 * Merchant Service - Admin Side
 * API calls for merchant management
 */
import api from './api';

/**
 * Get list of all merchants with filtering and pagination
 * @param {Object} params - Query parameters
 * @returns {Promise} - API response with merchant list
 */
export const getMerchants = async (params = {}) => {
  try {
    const response = await api.get('/admin/merchants/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching merchants:', error);
    throw error;
  }
};

/**
 * Get merchant statistics
 * @returns {Promise} - API response with merchant statistics
 */
export const getMerchantStatistics = async () => {
  try {
    const response = await api.get('/admin/merchants/statistics/');
    return response.data;
  } catch (error) {
    console.error('Error fetching merchant statistics:', error);
    throw error;
  }
};

/**
 * Get detailed merchant information
 * @param {string} merchantSid - Merchant SID
 * @returns {Promise} - API response with merchant details
 */
export const getMerchantDetails = async (merchantSid) => {
  try {
    const response = await api.get(`/admin/merchants/${merchantSid}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching merchant details:', error);
    throw error;
  }
};

/**
 * Update merchant status (approve, suspend, reject, reactivate)
 * @param {string} merchantSid - Merchant SID
 * @param {string} action - Action to perform (approve, suspend, reject, reactivate)
 * @param {string} reason - Reason for action (required for suspend/reject)
 * @returns {Promise} - API response
 */
export const updateMerchantStatus = async (merchantSid, action, reason = '') => {
  try {
    const response = await api.post(`/admin/merchants/${merchantSid}/status/`, {
      action,
      reason
    });
    return response.data;
  } catch (error) {
    console.error('Error updating merchant status:', error);
    throw error;
  }
};

/**
 * Approve merchant
 * @param {string} merchantSid - Merchant SID
 * @returns {Promise} - API response
 */
export const approveMerchant = async (merchantSid) => {
  return updateMerchantStatus(merchantSid, 'approve');
};

/**
 * Suspend merchant
 * @param {string} merchantSid - Merchant SID
 * @param {string} reason - Reason for suspension
 * @returns {Promise} - API response
 */
export const suspendMerchant = async (merchantSid, reason) => {
  return updateMerchantStatus(merchantSid, 'suspend', reason);
};

/**
 * Reject merchant
 * @param {string} merchantSid - Merchant SID
 * @param {string} reason - Reason for rejection
 * @returns {Promise} - API response
 */
export const rejectMerchant = async (merchantSid, reason) => {
  return updateMerchantStatus(merchantSid, 'reject', reason);
};

/**
 * Reactivate merchant
 * @param {string} merchantSid - Merchant SID
 * @returns {Promise} - API response
 */
export const reactivateMerchant = async (merchantSid) => {
  return updateMerchantStatus(merchantSid, 'reactivate');
};

/**
 * Review a merchant selling category request.
 * @param {string} merchantSid - Merchant SID
 * @param {number} requestId - Selling category request ID
 * @param {'approve'|'reject'} action - Review action
 * @param {string} rejectionReason - Required when rejecting
 * @returns {Promise} - API response
 */
export const reviewMerchantSellingCategory = async (
  merchantSid,
  requestId,
  action,
  rejectionReason = ''
) => {
  try {
    const response = await api.post(
      `/admin/merchants/${merchantSid}/selling-categories/${requestId}/review/`,
      {
        action,
        rejection_reason: rejectionReason
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error reviewing merchant selling category:', error);
    throw error;
  }
};

/**
 * Review a merchant legal representative identity document pair.
 * @param {string} merchantSid - Merchant SID
 * @param {'approve'|'reject'} action - Review action
 * @param {string} rejectionReason - Required when rejecting
 * @returns {Promise} - API response
 */
export const reviewMerchantIdentityDocuments = async (
  merchantSid,
  action,
  rejectionReason = ''
) => {
  try {
    const response = await api.post(
      `/admin/merchants/${merchantSid}/identity-documents/review/`,
      {
        action,
        rejection_reason: rejectionReason
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error reviewing merchant identity documents:', error);
    throw error;
  }
};

export default {
  getMerchants,
  getMerchantStatistics,
  getMerchantDetails,
  updateMerchantStatus,
  approveMerchant,
  suspendMerchant,
  rejectMerchant,
  reactivateMerchant,
  reviewMerchantSellingCategory,
  reviewMerchantIdentityDocuments
};
